import moment from 'moment';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import {
    isServerClientInSync,
    formatTimeOffset,
    getSystemTime,
    isServerBehindClient,
} from '../../../stores/SystemTimeStore';

import FilterRegistry from '../../page-framework/filter/FilterRegistry';
import { CUSTOM_LABEL, CUSTOM_ID, NOW_END_ID, LAST_60_MIN, getPeriod } from './TimeRangeFilterConstants';
import { alignTimeRangeWithGranularity, getAutoUpdateIntervalForTimeRange } from '../../../utilities/DateTimeUtilities';
import { timeRangeFormatter, getDefaultDateFormat } from '../../../utilities/FormattingUtilities';
import { filterId as timeRangeComparisonFilterId } from '../time-range-comparison-filter/TimeRangeComparisonFilterConstants';

export const title = t`Time Range`;

export function getPeriodTimeRange(period, realTime = false) {
    const end = getNow(realTime);
    // Subtract the last-n duration from the end time to arrive at the start time.
    const start = moment(end).subtract(period.durationInMinutes, 'minutes');
    // Align the range to the best granularity.
    const alignedRange = realTime ? { start, end } : alignTimeRangeWithGranularity(start, end);

    // Return the aligned UTC range for the query.
    return {
        start: alignedRange.start.toDate(),
        end: alignedRange.end.toDate(),
    };
}

/**
 * Parse the end time from the supplied values.
 *
 * @param id The time range id.
 * @param end The end time string to parse.
 *
 * @returns {Date}
 */
function parseEndTime(id, end, realTime = false) {
    if (end === NOW_END_ID && id !== CUSTOM_ID) {
        throw new Error(`${NOW_END_ID} is only valid for custom time ranges`);
    }

    return end === NOW_END_ID ? getNow(realTime).toDate() : new Date(parseInt(end, 10));
}

/**
 * Also used by time range comparison filter
 */
export function getTimeSyncMessage() {
    if (isServerClientInSync()) {
        return null;
    }

    const serverTime = moment(getSystemTime()).format(getDefaultDateFormat());
    const offset = formatTimeOffset();
    const aheadBehind = isServerBehindClient() ? i18n._(t`behind`) : i18n._(t`ahead of`);

    const infoMessage = t`The nGeniusPULSE server time is ${serverTime}, which is ${offset} ${aheadBehind} your client time.  All times are shown in nGeniusPULSE server time.`;

    return i18n._(infoMessage);
}

/**
 * Get the current time floored to the minute.
 *
 * @returns {Date}
 */
function getNow(realTime = false) {
    const now = getSystemTime();
    return realTime ? now : now.startOf('minute').subtract(1, 'minutes');
}

function isStale(state, refreshInterval, realTime = false) {
    const end = moment(state.end);
    const now = getNow(realTime);

    return end.add(refreshInterval, 'milliseconds').isSameOrBefore(now);
}

/**
 * Attempt to convert TimeRangeCOMPARISONFilter (if present) to regular TimeRangeFilter.  This is helpful when navigating from a comparison
 * panel which doesn't have a TimeRangeFilter, to a detail page which does.
 */
function convertTimeRangeComparisonFilterState(encodedFilterState, dataService) {
    if (!encodedFilterState?.[timeRangeComparisonFilterId]) return;

    const trcfStateService = FilterRegistry.getFilterStateService(timeRangeComparisonFilterId);
    const encodedTRCFState = encodedFilterState?.[timeRangeComparisonFilterId];
    const trcf = trcfStateService.createWithEncodedState(encodedTRCFState, dataService);

    if (!trcf) return;

    if (trcf.before.start && trcf.after.end) {
        return {
            // make the time range cover all of both our before & after time ranges
            id: CUSTOM_ID,
            start: trcf.before.start,
            end: trcf.after.end,
        };
    }
}

const TimeRangeFilterStateServiceFactory = (filterId, options) => {
    const TimeRangeFilterStateService = {
        /*
         * State factory functions
         */

        createWithEncodedState(encodedState) {
            const state = TimeRangeFilterStateService.decode(encodedState);

            // Ensure last-n time ranges are not stale.
            if (this.shouldRefreshState(state)) {
                const refreshInterval = getAutoUpdateIntervalForTimeRange(state, options?.realTime);
                if (isStale(state, refreshInterval, options?.realTime)) return this.refreshState(state);
            }

            return state;
        },

        createWithDefaultState() {
            return {
                id: LAST_60_MIN.id,
                ...getPeriodTimeRange(LAST_60_MIN),
            };
        },

        createState(encodedFilterState, dataService) {
            const encodedState = encodedFilterState?.[filterId];

            // If we're navigating from a comparison panel, we need to convert its TimeRangeComparisonFilter to an equivalent
            // TimeRangeFilter that can be used by the detail pages:
            if (!encodedState) {
                return (
                    convertTimeRangeComparisonFilterState(encodedFilterState, dataService) ||
                    this.createWithDefaultState()
                );
            }

            return this.createWithEncodedState(encodedState);
        },

        /**
         * Auto-update functions.
         */

        shouldRefreshState(currentState) {
            return getPeriod(currentState?.id) !== undefined;
        },

        getRefreshInterval(currentState) {
            const refreshInterval = getAutoUpdateIntervalForTimeRange(currentState, options?.realTime);

            // Refresh immediately if the current state is stale.
            if (isStale(currentState, refreshInterval, options?.realTime)) return 0;

            return refreshInterval;
        },

        refreshState(currentState) {
            // Nothing to refresh for custom time ranges, so just return the current state as a new
            // object so it triggers a state change.
            if (currentState.id === CUSTOM_ID) return { ...currentState };

            const period = getPeriod(currentState.id);

            if (period === undefined) {
                throw new Error(`Invalid refresh period ${currentState.id}`);
            }

            return {
                id: currentState.id,
                ...getPeriodTimeRange(period, options?.realTime),
            };
        },

        /*
         * Transformation functions
         */

        parameterize(state) {
            return {
                start: `${state.start.getTime()}`,
                end: `${state.end.getTime()}`,
            };
        },

        encode(state) {
            if (!state) return '';
            const start = new Date(state.start);
            const end = new Date(state.end);
            const startMs = start.getTime();
            const endMs = end.getTime();
            return `${state.id},${startMs},${endMs}`;
        },

        decode(encodedState) {
            const [id, startTimestamp, endTimestamp] = encodedState.split(',');
            let start;
            let end;

            // Some last-n states have only an ID, so we need to ensure the start/end are initialized.
            if (!startTimestamp || !endTimestamp) {
                const range = getPeriodTimeRange(getPeriod(id), options?.realTime);
                start = range.start;
                end = range.end;
            } else {
                start = new Date(parseInt(startTimestamp, 10));
                end = parseEndTime(id, endTimestamp, options?.realTime);
            }

            return {
                id,
                start,
                end,
            };
        },

        summarize(state) {
            const period = getPeriod(state.id);
            const periodName = period === undefined ? i18n._(CUSTOM_LABEL) : i18n._(period.name);
            const periodValue = timeRangeFormatter(state);

            return {
                title,
                value: `${periodName} ${periodValue}`,
                shortValue: period === undefined ? periodValue : periodName,
                infoMessage: getTimeSyncMessage(),
            };
        },
    };

    return TimeRangeFilterStateService;
};

export default TimeRangeFilterStateServiceFactory;
